import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULTS = {
  PAGE_SIZE: 20,
  DEBOUNCE_MS: 300,
  SCROLL_THRESHOLD: 10,
};

const arrayToById = (array, idKey = 'id') =>
  array.reduce((acc, item) => ({ ...acc, [item[idKey]]: item }), {});

/**
 * Generic hook for managing paginated search with infinite scroll.
 *
 * @param {Object} params - Hook parameters
 * @param {Function} params.fetchFn - Async function to fetch data. Receives { from, pageSize, searchTerm, ids }
 * @param {Function} params.parseResponse - Function to parse API response. Must return { items: [], total: number }
 * @param {string[]} [params.initialIds=[]] - IDs to preload on first render
 * @param {Array} [params.deps=[]] - Dependencies that trigger a refetch when changed
 * @param {number} [params.pageSize=20] - Number of items per page
 * @param {number} [params.debounceMs=300] - Debounce delay for search in milliseconds
 * @param {number} [params.scrollThreshold=10] - Pixels from bottom to trigger load more
 * @param {boolean} [params.enabled=true] - Whether fetching is enabled
 *
 * @returns {Object} Hook return value
 * @returns {Array} items - Array of fetched items
 * @returns {Object} itemsById - Map of items by ID
 * @returns {boolean} loading - Whether initial fetch is in progress
 * @returns {boolean} loadingMore - Whether pagination fetch is in progress
 * @returns {boolean} hasMore - Whether more items are available
 * @returns {number} total - Total number of items
 * @returns {Function} loadMore - Load next page of items
 * @returns {Function} search - Search items by term (debounced)
 * @returns {Function} reset - Reset to initial state
 * @returns {Function} handlePopupScroll - Scroll event handler for infinite scroll
 *
 * @example
 * // Basic usage with Elasticsearch service
 * const useScheduleTemplates = ({ orgId, divisionIds, templateIds, exactMatch, pageSize = 10 }) => {
 *   const service = useMemo(() => new ScheduleTemplateService(), []);
 *
 *   return usePaginatedSearch({
 *     fetchFn: ({ from, pageSize: size, searchTerm, ids }) =>
 *       service.searchElastic({
 *         orgId,
 *         divsIds: divisionIds,
 *         pageSize: size,
 *         from,
 *         searchTerm,
 *         ids,
 *         exactMatch,
 *       }),
 *     parseResponse: result => ({
 *       items: result?.hits?.hits?.map(hit => ({ ...hit._source, id: hit._id })) || [],
 *       total: result?.hits?.total?.value || 0,
 *     }),
 *     initialIds: templateIds,
 *     deps: [orgId, JSON.stringify(divisionIds)],
 *     pageSize,
 *     enabled: !!orgId,
 *   });
 * };
 *
 * @example
 * // Usage in a component with BaseSelector
 * const ScheduleTemplateSelector = ({ organizationId, divisionIds, templateIds, value, onChange }) => {
 *   const { items, loading, loadingMore, handlePopupScroll, search, reset } = useScheduleTemplates({
 *     orgId: organizationId,
 *     divisionIds,
 *     templateIds,
 *   });
 *
 *   return (
 *     <BaseSelector
 *       mode="multiple"
 *       items={items}
 *       loading={loading}
 *       loadingMore={loadingMore}
 *       handlePopupScroll={handlePopupScroll}
 *       onSearch={search}
 *       onReset={reset}
 *       value={value}
 *       onChange={onChange}
 *       placeholderId="scheduleTemplateSelector.selectTemplates"
 *       allLabelId="scheduleTemplateSelector.allTemplates"
 *       showAllOption
 *     />
 *   );
 * };
 */
export const usePaginatedSearch = ({
  fetchFn,
  parseResponse,
  initialIds = [],
  deps = [],
  pageSize = DEFAULTS.PAGE_SIZE,
  debounceMs = DEFAULTS.DEBOUNCE_MS,
  scrollThreshold = DEFAULTS.SCROLL_THRESHOLD,
  enabled = true,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const itemsRef = useRef([]);
  const fromRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const searchTermRef = useRef('');
  const debounceRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasConsumedInitialIds = useRef(false);
  const isInitializedRef = useRef(false);
  const prevDepsRef = useRef(null);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const itemsById = useMemo(() => arrayToById(items), [items]);

  const fetchItems = useCallback(
    async ({ reset = true, searchTerm = '', idsToPreload = [] }) => {
      if (!enabled) {
        if (reset) {
          setItems([]);
          setHasMore(false);
          setTotal(0);
        }
        return { items: [], total: 0 };
      }

      if (!reset && (!hasMoreRef.current || loadingMoreRef.current || loadingRef.current)) {
        return { items: itemsRef.current, total };
      }

      reset ? setLoading(true) : setLoadingMore(true);

      try {
        const currentFrom = reset ? 0 : fromRef.current;
        const shouldPreload = reset && idsToPreload?.length > 0;

        const result = await fetchFn({
          from: currentFrom,
          pageSize,
          searchTerm,
          ids: shouldPreload ? idsToPreload : undefined,
        });

        if (!isMountedRef.current) return { items: [], total: 0 };

        const { items: newItems, total: totalValue } = parseResponse(result);
        const newFrom = currentFrom + newItems.length;

        let updatedItems;
        if (reset) {
          updatedItems = newItems;
        } else {
          const uniqueMap = new Map();
          [...itemsRef.current, ...newItems].forEach(item => {
            if (!uniqueMap.has(item.id)) uniqueMap.set(item.id, item);
          });
          updatedItems = Array.from(uniqueMap.values());
        }

        setItems(updatedItems);
        fromRef.current = newFrom;
        setHasMore(newFrom < totalValue);
        setTotal(totalValue);
        searchTermRef.current = searchTerm;

        if (shouldPreload) hasConsumedInitialIds.current = true;

        return { items: updatedItems, total: totalValue };
      } catch (error) {
        console.error(`Error ${reset ? 'fetching' : 'loading more'} items:`, error);
        if (reset) {
          setItems([]);
          setTotal(0);
        }
        setHasMore(false);
        return { items: [], total: 0 };
      } finally {
        reset ? setLoading(false) : setLoadingMore(false);
      }
    },
    [enabled, fetchFn, parseResponse, pageSize, total],
  );

  const loadMore = useCallback(() => {
    fetchItems({ reset: false, searchTerm: searchTermRef.current });
  }, [fetchItems]);

  const search = useCallback(
    searchTerm => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return new Promise(resolve => {
        debounceRef.current = setTimeout(async () => {
          const result = await fetchItems({ reset: true, searchTerm });
          resolve(result);
        }, debounceMs);
      });
    },
    [fetchItems, debounceMs],
  );

  const reset = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    searchTermRef.current = '';
    fetchItems({ reset: true, searchTerm: '' });
  }, [fetchItems]);

  const handlePopupScroll = useCallback(
    e => {
      const { target } = e;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - scrollThreshold) {
        loadMore();
      }
    },
    [loadMore, scrollThreshold],
  );

  // Initialization and deps change effect
  useEffect(() => {
    if (!enabled) return;

    const depsString = JSON.stringify(deps);
    const depsChanged = prevDepsRef.current !== null && prevDepsRef.current !== depsString;

    const shouldPreload = initialIds?.length > 0 && !hasConsumedInitialIds.current;

    if (shouldPreload) {
      fetchItems({ reset: true, searchTerm: '', idsToPreload: initialIds });
      isInitializedRef.current = true;
    } else if (!isInitializedRef.current) {
      fetchItems({ reset: true, searchTerm: '' });
      isInitializedRef.current = true;
    } else if (depsChanged) {
      fetchItems({ reset: true, searchTerm: '' });
    }

    prevDepsRef.current = depsString;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return {
    items,
    itemsById,
    loading,
    loadingMore,
    hasMore,
    total,
    loadMore,
    search,
    reset,
    handlePopupScroll,
  };
};

